import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { DashboardConfig, GuestTokenResponse } from '../types/dashboard';

/**
 * Fetch the embed UUID for a given Superset dashboard.
 * Endpoint: GET /api/superset/dashboard/:id/config
 */
export function useDashboardConfig(dashboardId = '1') {
  return useQuery<DashboardConfig>({
    queryKey: ['superset-config', dashboardId],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/superset/dashboard/${encodeURIComponent(dashboardId)}/config`,
      );
      return data.data as DashboardConfig;
    },
    staleTime: Infinity, // embed UUID won't change in a session
    retry: 1,
  });
}

/**
 * Fetch a Superset guest token for a given embed UUID.
 * Endpoint: GET /api/superset/guest-token/:embedId
 *
 * Returns a function (not a hook) so the Superset SDK can call it
 * on demand when refreshing the token.
 */
export function useGuestTokenFetcher(embedId: string | undefined) {
  /**
   * The SDK's `fetchGuestToken` callback is invoked initially and on
   * subsequent refreshes.  We return a closure that always hits the
   * backend so the token stays fresh.
   */
  const fetchGuestToken = async (): Promise<string> => {
    if (!embedId) throw new Error('Embed ID not available');
    const { data } = await api.get(`/api/superset/guest-token/${encodeURIComponent(embedId)}`);
    const payload = data.data as GuestTokenResponse;
    if (!payload?.token) throw new Error('Invalid guest token response');
    return payload.token;
  };

  return fetchGuestToken;
}
